import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tramiteTareasService } from '../services/tramiteTareasService';
import type { TramiteTareaFormData } from '../types';

export const tramiteTareasQueryKey = (tramiteId: string | number) =>
  ['tramite-tareas', String(tramiteId)] as const;

interface UseTramiteTareasOptions {
  enabled?: boolean;
  /** Se llama después de cada mutación exitosa, además de invalidar la query
   * propia del modal. Lo usa la grilla/kanban para refrescar sus contadores. */
  onChanged?: () => void;
}

/** Checklist de tareas de una escritura puntual. */
export const useTramiteTareas = (
  tramiteId: string | number | undefined,
  options: UseTramiteTareasOptions = {}
) => {
  const { enabled = true, onChanged } = options;
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: tramiteTareasQueryKey(tramiteId ?? ''),
    queryFn: () => tramiteTareasService.listar(tramiteId as string | number),
    enabled: enabled && Boolean(tramiteId),
  });

  const invalidar = () => {
    if (tramiteId) {
      queryClient.invalidateQueries({ queryKey: tramiteTareasQueryKey(tramiteId) });
    }
    onChanged?.();
  };

  const crearMutation = useMutation({
    mutationFn: (payload: TramiteTareaFormData) =>
      tramiteTareasService.crear(tramiteId as string | number, payload),
    onSuccess: invalidar,
  });

  const actualizarMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TramiteTareaFormData> }) =>
      tramiteTareasService.actualizar(tramiteId as string | number, id, data),
    onSuccess: invalidar,
  });

  const eliminarMutation = useMutation({
    mutationFn: (id: string) => tramiteTareasService.eliminar(tramiteId as string | number, id),
    onSuccess: invalidar,
  });

  const sincronizarMutation = useMutation({
    mutationFn: () => tramiteTareasService.sincronizarBase(tramiteId as string | number),
    onSuccess: invalidar,
  });

  return {
    tareas: data?.tareas ?? [],
    resumen: data?.resumen ?? { total: 0, completadas: 0, porcentaje: 0 },
    isLoading,
    error,
    crearTarea: crearMutation.mutateAsync,
    actualizarTarea: actualizarMutation.mutateAsync,
    eliminarTarea: eliminarMutation.mutateAsync,
    sincronizarBase: sincronizarMutation.mutateAsync,
    isGuardando:
      crearMutation.isPending ||
      actualizarMutation.isPending ||
      eliminarMutation.isPending ||
      sincronizarMutation.isPending,
  };
};

export default useTramiteTareas;
